import { Instagram } from "lucide-react";
import type { ReactNode } from "react";
import type { SocialProviderKey } from "@/lib/social-provider-catalog";

function SvgIcon({
	viewBox,
	d,
	className,
}: {
	viewBox: string;
	d: string;
	className?: string;
}) {
	return (
		<svg
			viewBox={viewBox}
			fill="currentColor"
			className={className}
			aria-hidden="true"
		>
			<path d={d} />
		</svg>
	);
}

export type PlatformKey = SocialProviderKey;

export const PLATFORM_ICONS: Record<
	PlatformKey,
	(className: string) => ReactNode
> = {
	facebook: (c) => (
		<SvgIcon
			viewBox="0 0 24 24"
			d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
			className={c}
		/>
	),
	threads: (c) => (
		<SvgIcon
			viewBox="0 0 24 24"
			d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.734 7.847c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.321.142 1.49.7 2.58 1.761 3.154 3.07.797 1.82.871 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Zm1.003-11.69c-.242 0-.487.007-.739.021-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.215-.221z"
			className={c}
		/>
	),
	youtube: (c) => (
		<SvgIcon
			viewBox="0 0 24 24"
			d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
			className={c}
		/>
	),
	pinterest: (c) => (
		<SvgIcon
			viewBox="0 0 24 24"
			d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"
			className={c}
		/>
	),
	bluesky: (c) => (
		<SvgIcon
			viewBox="0 0 568 501"
			d="M123.121 33.6637C188.241 82.5526 258.281 181.681 284 234.873C309.719 181.681 379.759 82.5526 444.879 33.6637C491.866 -1.61183 568 -28.9064 568 57.9464C568 75.2916 558.055 189.033 552.194 210.83C534.23 275.349 462.956 290.485 398.322 280.108C507.473 297.551 535.341 363.71 474.315 429.868C356.729 557.474 298.591 404.094 287.308 371.245C285.891 367.137 284.74 364.371 284 364.371C283.26 364.371 282.109 367.137 280.692 371.245C269.409 404.094 211.271 557.474 93.6847 429.868C32.659 363.71 60.5266 297.551 169.678 280.108C105.044 290.485 33.7698 275.349 15.8058 210.83C9.94524 189.033 0 75.2916 0 57.9464C0 -28.9064 76.1341 -1.61183 123.121 33.6637Z"
			className={c}
		/>
	),
	linkedin: (c) => (
		<SvgIcon
			viewBox="0 0 24 24"
			d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
			className={c}
		/>
	),
	tiktok: (c) => (
		<SvgIcon
			viewBox="0 0 24 24"
			d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.98a8.21 8.21 0 0 0 4.76 1.52V7.05a4.84 4.84 0 0 1-1-.36z"
			className={c}
		/>
	),
	x: (c) => (
		<SvgIcon
			viewBox="0 0 24 24"
			d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
			className={c}
		/>
	),
	instagram: (c) => <Instagram className={c} />,
};

export const PLATFORM_COLORS: Record<
	PlatformKey,
	{ text: string; bg: string; solidBg: string; solidText: string }
> = {
	bluesky: {
		text: "text-blue-500",
		bg: "bg-blue-500/10",
		solidBg: "bg-blue-500",
		solidText: "text-white",
	},
	facebook: {
		text: "text-[#1877F2]",
		bg: "bg-[#1877F2]/10",
		solidBg: "bg-[#1877F2]",
		solidText: "text-white",
	},
	instagram: {
		text: "text-pink-500",
		bg: "bg-pink-500/10",
		solidBg: "bg-pink-500",
		solidText: "text-white",
	},
	linkedin: {
		text: "text-blue-600",
		bg: "bg-blue-600/10",
		solidBg: "bg-blue-600",
		solidText: "text-white",
	},
	pinterest: {
		text: "text-red-600",
		bg: "bg-red-600/10",
		solidBg: "bg-red-600",
		solidText: "text-white",
	},
	threads: {
		text: "text-foreground",
		bg: "bg-neutral-500/10",
		solidBg: "bg-foreground",
		solidText: "text-background",
	},
	tiktok: {
		text: "text-foreground",
		bg: "bg-foreground/10",
		solidBg: "bg-foreground",
		solidText: "text-background",
	},
	x: {
		text: "text-foreground",
		bg: "bg-foreground/10",
		solidBg: "bg-foreground",
		solidText: "text-background",
	},
	youtube: {
		text: "text-red-500",
		bg: "bg-red-500/10",
		solidBg: "bg-red-500",
		solidText: "text-white",
	},
};
